/**
 * Campaign Switch Tracker
 *
 * Tracks pending campaign switches and resolves/rejects promises
 * when Playjector reports success or failure via pavilionLaunchReply.
 */

import type { PavilionLauncResults } from '@playcastdotio/CoreTypes';

export type CampaignSwitchResult =
  | { success: true }
  | { success: false; reason: string; transient?: boolean };

type PendingSwitch = {
  campaignId: string;
  resolve: (result: CampaignSwitchResult) => void;
  timeoutId: NodeJS.Timeout;
};

/** Failure outcomes that must not clear campaign or notify realtime-api as permanent failures */
export function isTransientCampaignSwitchAbort(result: CampaignSwitchResult): boolean {
  if (result.success) {
    return false;
  }
  return (
    result.transient === true ||
    result.reason === 'cancelled' ||
    result.reason === 'superseded'
  );
}

class CampaignSwitchTracker {
  private pendingSwitch: PendingSwitch | null = null;

  /**
   * Start tracking a campaign switch
   * Returns a promise that resolves when pavilionLaunchReply is received or times out
   */
  startSwitch(campaignId: string, timeoutMs: number): Promise<CampaignSwitchResult> {
    // Clear any existing pending switch
    if (this.pendingSwitch) {
      console.warn('[Campaign Switch] Clearing previous pending switch (superseded)');
      clearTimeout(this.pendingSwitch.timeoutId);
      this.pendingSwitch.resolve({ success: false, reason: 'superseded', transient: true });
    }

    return new Promise((resolve) => {
      const startTime = Date.now();
      const timeoutId = setTimeout(() => {
        const elapsed = Date.now() - startTime;
        console.error(`[Campaign Switch] ⏰ TIMEOUT fired after ${elapsed}ms (expected ${timeoutMs}ms)`);
        this.pendingSwitch = null;
        resolve({ success: false, reason: 'timeout' });
      }, timeoutMs);

      this.pendingSwitch = {
        campaignId,
        resolve,
        timeoutId,
      };

      console.log(`[Campaign Switch] Started tracking switch to ${campaignId} with ${timeoutMs}ms timeout`);
    });
  }

  /**
   * Report pavilionLaunchReply result
   */
  reportResult(result: PavilionLauncResults, reason?: string): void {
    if (!this.pendingSwitch) {
      console.warn(`[Campaign Switch] ⚠️ Received pavilionLaunchReply (${result}) but no pending switch - timeout already fired?`);
      return;
    }

    clearTimeout(this.pendingSwitch.timeoutId);
    console.log(`[Campaign Switch] ✅ reportResult called, timeout cleared. Result: ${result}`);

    if (result === 'SUCCESS') {
      console.log('[Campaign Switch] 🎉 Game launch SUCCESS - resolving promise');
      this.pendingSwitch.resolve({ success: true });
    } else {
      // All other results (FAILURE, SECURITY_FAILURE, etc.) are treated as failures
      console.error('[Campaign Switch] ❌ Game launch failed:', result, reason || '');
      this.pendingSwitch.resolve({
        success: false,
        reason: reason || result
      });
    }

    this.pendingSwitch = null;
  }

  /**
   * Cancel pending switch (e.g., on disconnect or manual cancellation)
   */
  cancelSwitch(): void {
    if (this.pendingSwitch) {
      console.warn('[Campaign Switch] Cancelling pending switch');
      clearTimeout(this.pendingSwitch.timeoutId);
      this.pendingSwitch.resolve({ success: false, reason: 'cancelled', transient: true });
      this.pendingSwitch = null;
    }
  }

  /**
   * Check if there's a pending switch
   */
  hasPendingSwitch(): boolean {
    return this.pendingSwitch !== null;
  }

  /**
   * Get the campaign ID of the pending switch
   */
  getPendingCampaignId(): string | null {
    return this.pendingSwitch?.campaignId ?? null;
  }
}

// Singleton instance
export const campaignSwitchTracker = new CampaignSwitchTracker();

